import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leadsApi, Lead } from '../api/leads';
import { tasksApi } from '../api/tasks';
import { activitiesApi } from '../api/activities';
import { accountsApi } from '../api/accounts';
import { contactsApi } from '../api/contacts';
import { useAuth } from '../context/AuthContext';
import PremiumLayout from '../components/PremiumLayout';
import Tooltip from '../components/Tooltip';
import { formatCurrency, Currency } from '../utils/currencyUtils';
import { parseLeadValue, parseLeadProbability } from '../utils/leadUtils';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  PlusIcon,
  PencilIcon,
  DocumentTextIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

// Stage definitions matching Leads.tsx
const stages = [
  { id: 'new', name: 'New', color: 'bg-slate-500', tooltip: 'Fresh lead - not yet contacted' },
  { id: 'contacted', name: 'Contacted', color: 'bg-blue-500', tooltip: 'Initial contact made' },
  { id: 'first_meeting', name: 'First Meeting', color: 'bg-cyan-500', tooltip: 'First meeting completed' },
  { id: 'second_meeting', name: 'Second Meeting', color: 'bg-teal-500', tooltip: 'Second meeting completed' },
  { id: 'qualified', name: 'Qualified', color: 'bg-emerald-500', tooltip: 'Lead qualified - set A/B/C category' },
  { id: 'pitch_presented', name: 'Pitch Presented', color: 'bg-amber-500', tooltip: 'Pitch/presentation delivered' },
  { id: 'sow_in_progress', name: 'SOW in Progress', color: 'bg-orange-500', tooltip: 'Preparing Statement of Work' },
  { id: 'sow_sent', name: 'SOW Sent', color: 'bg-pink-500', tooltip: 'Proposal sent to client' },
  { id: 'clarification_call', name: 'Clarification Call', color: 'bg-fuchsia-500', tooltip: 'Addressing client questions' },
  { id: 'roi_proof_call', name: 'ROI / Proof Call', color: 'bg-purple-500', tooltip: 'ROI discussion completed' },
  { id: 'decision_stage', name: 'Decision Stage', color: 'bg-violet-500', tooltip: 'Client making final decision' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-indigo-500', tooltip: 'Negotiating terms' },
  { id: 'won', name: 'Won', color: 'bg-green-500', tooltip: 'Deal closed successfully! 🎉' },
  { id: 'lost', name: 'Lost', color: 'bg-red-500', tooltip: 'Deal lost - record reason' },
];

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'activities' | 'tasks'>('overview');

  const leadId = parseInt(id || '0');

  const { data: lead, isLoading: leadLoading } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsApi.getById(leadId),
    enabled: !!leadId,
  });
  
  // Debug: Log lead data to see if lost_reason is present
  React.useEffect(() => {
    if (lead) {
      console.log('🔍 LeadDetail - Lead data:', {
        id: lead.id,
        name: lead.name,
        stage: lead.stage,
        lost_reason: lead.lost_reason,
        lost_date: lead.lost_date,
        fullLead: lead
      });
    }
  }, [lead]);

  const { data: tasks = [] } = useQuery({
    queryKey: ['tasks'],
    queryFn: tasksApi.getAll,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['activities'],
    queryFn: activitiesApi.getAll,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.getAll,
  });

  // Filter tasks and activities for this lead
  const leadTasks = tasks.filter(t => t.lead_id === leadId);
  const leadActivities = activities.filter(a => a.lead_id === leadId);

  // Get related account and contact
  const account = accounts.find(a => a.id === lead?.account_id);
  const contact = contacts.find(c => c.id === lead?.primary_contact_id);
  const decisionMakerContact = contacts.find(c => c.id === lead?.decision_maker_contact_id);

  if (leadLoading) {
    return (
      <PremiumLayout title="Lead Details">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (!lead) {
    return (
      <PremiumLayout title="Lead Not Found">
        <div className="text-center py-12">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Lead not found</h2>
          <p className="text-gray-500 mb-4">The lead you're looking for doesn't exist or you don't have access.</p>
          <Link to="/leads" className="btn-primary">
            Back to Leads
          </Link>
        </div>
      </PremiumLayout>
    );
  }

  const stageInfo = stages.find(s => s.id === lead.stage) || stages[0];
  const stageIndex = stages.findIndex(s => s.id === lead.stage);
  const progressPercent = Math.round(((stageIndex + 1) / stages.length) * 100);
  const createdDate = lead.date_created || lead.created_at;
  const opportunityAge = createdDate ? Math.floor((Date.now() - new Date(createdDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <PremiumLayout
      title=""
      actions={
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/leads')}
            className="btn-secondary text-sm px-4 py-2"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Leads
          </button>
          <Link to={`/leads?edit=${lead.id}`} className="btn-primary text-sm px-4 py-2">
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit Lead
          </Link>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header Card - Brand/Company First */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <BuildingOfficeIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{lead.account_name || lead.brand || lead.name}</h1>
                {(lead.first_name || lead.last_name || lead.lead_name) && (
                  <p className="text-gray-600 flex items-center mt-1">
                    <UserIcon className="h-4 w-4 mr-1.5" />
                    {lead.first_name && lead.last_name
                      ? `${lead.first_name} ${lead.last_name}`
                      : lead.first_name || lead.lead_name || ''}
                  </p>
                )}
                <div className="flex items-center space-x-4 mt-2">
                  {lead.industry && (
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{lead.industry}</span>
                  )}
                  {lead.geography && (
                    <span className="text-sm text-gray-500">{lead.geography}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <Tooltip content={stageInfo.tooltip} position="left">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${stageInfo.color} text-white`}>
                  {stageInfo.name}
                </span>
              </Tooltip>
              {lead.category && (
                <Tooltip content={`Category ${lead.category}: ${lead.category === 'A' ? 'Hot / High Priority' : lead.category === 'B' ? 'Warm / Medium Priority' : 'Nurture / Low Priority'}`} position="left">
                  <span className={`ml-2 inline-flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm text-white ${
                    lead.category === 'A' ? 'bg-emerald-500' : 
                    lead.category === 'B' ? 'bg-amber-500' : 'bg-gray-500'
                  }`}>
                    {lead.category}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Pipeline Progress</span>
              <span className="text-sm text-gray-500">{progressPercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${stageInfo.color}`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-400">
              <span>New</span>
              <span>Qualified</span>
              <span>SOW</span>
              <span>Close</span>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tooltip content="Total deal value for this opportunity" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CurrencyRupeeIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    {formatCurrency(parseLeadValue(lead.value), 'INR')}
                  </div>
                  <div className="text-xs text-gray-500">Deal Value</div>
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip content="Likelihood of closing this deal based on stage and history" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{parseLeadProbability(lead.probability)}%</div>
                  <div className="text-xs text-gray-500">Close Probability</div>
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip content="Days since this lead was created" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">{opportunityAge}</div>
                  <div className="text-xs text-gray-500">Days in Pipeline</div>
                </div>
              </div>
            </div>
          </Tooltip>

          <Tooltip content="Expected close date for this deal" position="bottom">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-help">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <ClockIcon className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    {lead.expected_close_date 
                      ? new Date(lead.expected_close_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                      : '—'
                    }
                  </div>
                  <div className="text-xs text-gray-500">Expected Close</div>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'overview', label: 'Overview', icon: InformationCircleIcon },
                { id: 'activities', label: `Activities (${leadActivities.length})`, icon: DocumentTextIcon },
                { id: 'tasks', label: `Tasks (${leadTasks.length})`, icon: CheckCircleIcon },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Lead Information */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Lead Information</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Brand / Company', value: lead.account_name || lead.brand || lead.name },
                      { 
                        label: 'Contact Name', 
                        value: lead.first_name && lead.last_name
                          ? `${lead.first_name} ${lead.last_name}`
                          : lead.first_name || lead.lead_name || lead.name 
                      },
                      { label: 'Project Name', value: lead.project_name },
                      { label: 'Industry', value: lead.industry },
                      { label: 'Brand Type', value: lead.brand_type },
                      { label: 'Geography', value: lead.geography },
                      { label: 'Lead Source', value: lead.lead_source || lead.source },
                      { label: 'Service Line', value: lead.service_line },
                      { label: 'Description', value: lead.description },
                      { label: 'Created Date', value: createdDate ? new Date(createdDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                      { label: 'Last Contacted', value: lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                      { label: 'Updated Date', value: lead.updated_at ? new Date(lead.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                    ].map((item, idx) => item.value && (
                      <div key={idx} className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">{item.label}</span>
                        <span className="text-sm font-medium text-gray-900">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deal Details */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Deal Details</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Deal Value', value: formatCurrency(parseLeadValue(lead.value), 'INR') },
                      { label: 'Deal Value (Original)', value: lead.deal_value != null ? formatCurrency(parseLeadValue(lead.deal_value), 'INR') : null },
                      { label: 'Forecasted Value', value: formatCurrency(parseLeadValue(lead.value) * parseLeadProbability(lead.probability) / 100, 'INR') },
                      { label: 'Close Probability', value: `${parseLeadProbability(lead.probability)}%` },
                      { label: 'Category', value: lead.category ? `${lead.category} - ${lead.category === 'A' ? 'Hot' : lead.category === 'B' ? 'Warm' : 'Nurture'}` : null },
                      { label: 'Expected Close Date', value: lead.expected_close_date ? new Date(lead.expected_close_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                      { label: 'Next Action', value: lead.next_action },
                      { label: 'Next Action Date', value: lead.next_action_date ? new Date(lead.next_action_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                      { label: 'Urgency Score', value: lead.urgency_score ? `${lead.urgency_score}/20` : null },
                      { label: 'Competitor', value: lead.competitor },
                      { label: 'Close Plan Created', value: lead.close_plan_id ? 'Yes' : 'No' },
                      { label: 'Won Date', value: lead.stage === 'won' && lead.won_date ? new Date(lead.won_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                      { label: 'Lost Date', value: lead.stage === 'lost' && lead.lost_date ? new Date(lead.lost_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null },
                    ].map((item, idx) => item.value && (
                      <div key={idx} className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">{item.label}</span>
                        <span className="text-sm font-medium text-gray-900">{item.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Related Account */}
                  {account && (
                    <div className="mt-6">
                      <h4 className="text-sm font-bold text-gray-700 mb-2">Related Account</h4>
                      <Link 
                        to={`/accounts/${account.id}`}
                        className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <BuildingOfficeIcon className="h-5 w-5 text-indigo-500 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">{account.name}</div>
                          {account.industry && <div className="text-xs text-gray-500">{account.industry}</div>}
                        </div>
                      </Link>
                    </div>
                  )}

                  {/* Related Contact */}
                  {contact && (
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-2">Primary Contact</h4>
                      <Link 
                        to={`/contacts?search=${contact.first_name}`}
                        className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <UserIcon className="h-5 w-5 text-blue-500 mr-3" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{contact.first_name} {contact.last_name}</div>
                          <div className="text-xs text-gray-500 space-y-0.5 mt-1">
                            {contact.title && <div>{contact.title}</div>}
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="hover:text-indigo-600 block" onClick={(e) => e.stopPropagation()}>
                                {contact.email}
                              </a>
                            )}
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="hover:text-indigo-600 block" onClick={(e) => e.stopPropagation()}>
                                {contact.phone}
                              </a>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  )}

                  {/* Decision Maker Contact */}
                  {decisionMakerContact && (
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-gray-700 mb-2">Decision Maker Contact</h4>
                      <Link 
                        to={`/contacts?search=${decisionMakerContact.first_name}`}
                        className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <UserIcon className="h-5 w-5 text-purple-500 mr-3" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{decisionMakerContact.first_name} {decisionMakerContact.last_name}</div>
                          <div className="text-xs text-gray-500 space-y-0.5 mt-1">
                            {decisionMakerContact.title && <div>{decisionMakerContact.title}</div>}
                            {decisionMakerContact.email && (
                              <a href={`mailto:${decisionMakerContact.email}`} className="hover:text-indigo-600 block" onClick={(e) => e.stopPropagation()}>
                                {decisionMakerContact.email}
                              </a>
                            )}
                            {decisionMakerContact.phone && (
                              <a href={`tel:${decisionMakerContact.phone}`} className="hover:text-indigo-600 block" onClick={(e) => e.stopPropagation()}>
                                {decisionMakerContact.phone}
                              </a>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>

                {/* Decision Making & Strategy */}
                <div className="lg:col-span-2">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Decision Making & Strategy</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3">Decision Makers</h4>
                      <div className="space-y-3">
                        {[
                          { label: 'Decision Maker', value: lead.decision_maker },
                          { label: 'Decision Maker Level', value: lead.decision_maker_level },
                          { label: 'Influencers', value: lead.influencers },
                        ].map((item, idx) => item.value && (
                          <div key={idx} className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500">{item.label}</span>
                            <span className="text-sm font-medium text-gray-900">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3">Strategy & Positioning</h4>
                      <div className="space-y-3">
                        {[
                          { label: 'Problem Statement', value: lead.problem_statement },
                          { label: 'Solution Positioning', value: lead.solution_positioning },
                          { label: 'Competitor', value: lead.competitor },
                        ].map((item, idx) => item.value && (
                          <div key={idx} className="py-2 border-b border-gray-100">
                            <span className="text-sm text-gray-500 block mb-1">{item.label}</span>
                            <span className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                {(lead.notes || lead.description || lead.reason || (lead.stage === 'lost' && lead.lost_reason)) && (
                  <div className="lg:col-span-2">
                    {lead.description && (
                      <div className="mb-4">
                        <h4 className="text-sm font-bold text-gray-700 mb-2">Description</h4>
                        <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{lead.description}</p>
                      </div>
                    )}
                    {lead.notes && (
                      <div className="mb-4">
                        <h4 className="text-sm font-bold text-gray-700 mb-2">Notes</h4>
                        <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{lead.notes}</p>
                      </div>
                    )}
                    {lead.stage === 'lost' && lead.lost_reason && (
                      <div className="mb-4">
                        <h4 className="text-sm font-bold text-red-700 mb-2">Lost Reason</h4>
                        <p className="text-sm text-gray-600 bg-red-50 p-4 rounded-lg border border-red-200 whitespace-pre-wrap">{lead.lost_reason}</p>
                      </div>
                    )}
                    {lead.reason && (
                      <div className="mb-4">
                        <h4 className="text-sm font-bold text-gray-700 mb-2">Reason (Win/Loss)</h4>
                        <p className="text-sm text-gray-600 bg-amber-50 p-4 rounded-lg whitespace-pre-wrap">{lead.reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activities' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Activity History</h3>
                  <Link to="/activities" className="btn-secondary text-sm">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Log Activity
                  </Link>
                </div>
                {leadActivities.length > 0 ? (
                  <div className="space-y-4">
                    {leadActivities.map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          activity.type === 'call' ? 'bg-blue-100' :
                          activity.type === 'email' ? 'bg-green-100' :
                          activity.type === 'meeting' ? 'bg-purple-100' : 'bg-gray-100'
                        }`}>
                          {activity.type === 'call' ? <PhoneIcon className="h-5 w-5 text-blue-600" /> :
                           activity.type === 'email' ? <EnvelopeIcon className="h-5 w-5 text-green-600" /> :
                           activity.type === 'meeting' ? <CalendarIcon className="h-5 w-5 text-purple-600" /> :
                           <DocumentTextIcon className="h-5 w-5 text-gray-600" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">{activity.subject}</h4>
                            <span className="text-xs text-gray-500">
                              {new Date(activity.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No activities logged yet. <Link to="/activities" className="text-indigo-600 hover:underline">Log your first activity</Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Tasks</h3>
                  <Link to="/tasks" className="btn-secondary text-sm">
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Task
                  </Link>
                </div>
                {leadTasks.length > 0 ? (
                  <div className="space-y-3">
                    {leadTasks.map((task) => (
                      <div key={task.id} className={`flex items-center justify-between p-4 rounded-lg border ${
                        task.status === 'completed' ? 'bg-green-50 border-green-200' :
                        task.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <CheckCircleIcon className={`h-5 w-5 ${
                            task.status === 'completed' ? 'text-green-500' :
                            task.status === 'in_progress' ? 'text-blue-500' : 'text-gray-400'
                          }`} />
                          <div>
                            <h4 className="font-medium text-gray-900">{task.title}</h4>
                            {task.due_date && (
                              <p className="text-xs text-gray-500">
                                Due: {new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No tasks yet. <Link to="/tasks" className="text-indigo-600 hover:underline">Create a task</Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PremiumLayout>
  );
};

export default LeadDetail;
